// Concurrent-match modal — visual, game-platform feel.
//
// Layout (per user spec, iteration 3):
//
//   ╭───────────────────────────────────────╮
//   │                              [×]      │  ← top-right close
//   │                                       │
//   │            [⚠ alert icon]             │  ← stays inside modal,
//   │                                       │     never clipped by parent
//   │           Already playing!            │
//   │                                       │
//   │  ╭─ Chess ─────────────────────────╮  │
//   │  │ [logo] Chess                    │  │  ← match card with
//   │  │        Live match               │  │     logo + name + status
//   │  │                                 │  │
//   │  │  Back to game        −₹100      │  │  ← labels in matching
//   │  │  [Continue]          [Quit]     │  │     colors above each button
//   │  ╰─────────────────────────────────╯  │
//   │                                       │
//   │  (one card per blocking match)        │
//   ╰───────────────────────────────────────╯
//
// Design rules (per all rounds of user feedback):
//   • Buttons themselves are SIMPLE — "Continue" and "Quit". The
//     descriptive label ("Back to game" / "−₹100") sits ABOVE each
//     button as separate text in the SAME COLOR as that button. Eyes
//     read top-to-bottom: see what'll happen, then see the button.
//   • The amount shown is the user's BET (what they put into the
//     wallet to enter), never the pot or winnings.
//   • Alert icon sits INSIDE the modal padding — no negative-margin
//     overflow tricks (those clip in landscape / on small screens).
//   • × close at top-right replaces the bottom Cancel button so
//     vertical space is preserved (critical for landscape Rummy).
//   • Whole modal max-h-[90vh] with internal scroll, so it always
//     fits even in tight landscape windows.
//
// Image files referenced:
//   /games/_shared/alert.png  — warning icon (placeholder; user-replaceable)
//   /games/{id}/Logo.png      — per-game logo (already exists)

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

type BlockingMatch = {
  game: string;
  displayName: string;
  sessionId: number | null;
  bet: number;
  isQueueOnly: boolean;
};

type Props = {
  open: boolean;
  blocking: BlockingMatch[] | null;
  walletBalance?: number;
  onResolved?: () => void;
  onCancel: () => void;
};

const fmt = (n: number) => Math.max(0, Math.floor(n)).toLocaleString('en-IN');

// One row per blocking match. Two columns inside; each column is a
// stacked (label, button) pair with matching colors.
function MatchRow({
  match, onResume, onQuit, submitting, confirmThisRow, onArmConfirm,
}: {
  match: BlockingMatch;
  onResume: (gameId: string) => void;
  onQuit: (gameId: string) => void;
  submitting: boolean;
  confirmThisRow: boolean;
  onArmConfirm: () => void;
}) {
  const bet = Number(match.bet || 0);
  const showLoss = !match.isQueueOnly && bet > 0;
  function handleQuitClick() {
    if (!showLoss) { onQuit(match.game); return; }
    if (confirmThisRow) { onQuit(match.game); return; }
    onArmConfirm();
  }
  return (
    <div className="rounded-2xl bg-neutral-900 border border-neutral-700 p-3 shadow-md">
      {/* TOP — game logo + name + match status */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-xl bg-neutral-800 border border-neutral-700 grid place-items-center overflow-hidden flex-none">
          <img
            src={`/games/${match.game}/Logo.png`}
            alt={match.displayName}
            className="w-full h-full object-contain p-1"
          />
        </div>
        <div className="text-left flex-1 min-w-0">
          <div className="text-[15px] font-black text-white tracking-tight">
            {match.displayName}
          </div>
          <div className="text-[10px] text-neutral-400 tracking-widest uppercase mt-0.5">
            {match.isQueueOnly ? 'In queue' : 'Live match'}
          </div>
        </div>
      </div>

      {/* BOTTOM — two columns, each with COLORED LABEL + BUTTON below */}
      <div className="grid grid-cols-2 gap-2.5">
        {/* LEFT — green column: "Back to game" label above Continue button */}
        <div className="flex flex-col items-center">
          <div className="text-emerald-300 font-bold text-[12px] sm:text-[13px] mb-1.5 text-center leading-tight">
            Back to game
          </div>
          <button
            type="button"
            onClick={() => onResume(match.game)}
            disabled={submitting}
            className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-black text-[13px] px-3 py-2.5 shadow-lg shadow-emerald-700/40 transition disabled:opacity-50 inline-flex items-center justify-center gap-1"
          >
            ▶ Continue
          </button>
        </div>

        {/* RIGHT — red column: "−₹X" or "No charge" label above Quit button */}
        <div className="flex flex-col items-center">
          {showLoss ? (
            <div className="text-rose-400 font-black text-[16px] sm:text-[18px] mb-1.5 leading-none tabular-nums">
              −₹{fmt(bet)}
            </div>
          ) : (
            <div className="text-emerald-300 font-bold text-[12px] mb-1.5 leading-tight">
              No charge
            </div>
          )}
          <button
            type="button"
            onClick={handleQuitClick}
            disabled={submitting}
            className={
              'w-full rounded-xl active:scale-95 text-white font-black text-[13px] px-3 py-2.5 shadow-lg transition disabled:opacity-50 inline-flex items-center justify-center gap-1 ' +
              (confirmThisRow
                ? 'bg-red-700 hover:bg-red-600 ring-2 ring-red-300 animate-pulse'
                : 'bg-rose-600 hover:bg-rose-500 shadow-rose-900/40')
            }
          >
            {confirmThisRow
              ? (showLoss ? `Yes, lose ₹${fmt(bet)}` : 'Yes, leave')
              : '✖ Quit'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ConcurrentMatchModal({
  open, blocking, onResolved, onCancel,
}: Props) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track which row's Quit button is in "are you sure?" stage.
  const [armedRow, setArmedRow] = useState<string | null>(null);
  const [alertImgFailed, setAlertImgFailed] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setError(null);
      setArmedRow(null);
    }
  }, [open]);

  if (!open) return null;
  const list = blocking || [];

  function handleResume(gameId: string) {
    onCancel();
    setTimeout(() => navigate(`/games/${gameId}`), 60);
  }

  async function handleQuit(_gameId: string) {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/me/quit-active-matches', {});
      await new Promise((r) => setTimeout(r, 350));
      if (onResolved) onResolved();
    } catch (e: any) {
      setError(e?.message || 'Quit failed — try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const portalHost = typeof document !== 'undefined' ? document.body : null;

  const node = (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[2147483646] flex items-center justify-center p-3 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.78)' }}
    >
      <div
        // max-w / max-h tuned for both portrait phone AND landscape tablet
        // (Rummy is landscape today; future games may be too).
        className="w-full max-w-sm rounded-3xl bg-gradient-to-b from-neutral-900 to-black border-2 border-rose-600 px-5 pt-5 pb-5 text-center relative max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: '0 0 30px rgba(244, 63, 94, 0.35), 0 30px 80px rgba(0,0,0,0.7)' }}
      >
        {/* TOP-RIGHT × — replaces the old bottom Cancel button so the
            modal saves vertical real estate (critical for landscape) */}
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          aria-label="Close"
          className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-neutral-800 hover:bg-neutral-700 text-neutral-300 hover:text-white grid place-items-center text-lg leading-none font-bold transition disabled:opacity-50"
        >
          ×
        </button>

        {/* ALERT ICON — INSIDE modal padding, no negative-margin overflow.
            Earlier versions used -mt-12 which clipped on small / landscape
            viewports; this version keeps the icon entirely within the
            modal's safe area so it ALWAYS renders fully. */}
        <div className="flex justify-center mb-3">
          <div
            className="w-16 h-16 rounded-full bg-rose-600 border-4 border-rose-900 grid place-items-center shadow-lg"
            style={{ animation: 'pulse 1.6s ease-in-out infinite' }}
          >
            {alertImgFailed ? (
              <span className="text-3xl text-white" aria-hidden>⚠</span>
            ) : (
              <img
                src="/games/_shared/alert.png"
                alt=""
                className="w-10 h-10 object-contain"
                onError={() => setAlertImgFailed(true)}
              />
            )}
          </div>
        </div>

        <div className="text-white text-lg font-black tracking-tight mb-4">
          Already playing!
        </div>

        {/* PER-MATCH ROWS */}
        <div className="space-y-3">
          {list.map((m, i) => (
            <MatchRow
              key={`${m.game}-${m.sessionId ?? 'q'}-${i}`}
              match={m}
              onResume={handleResume}
              onQuit={handleQuit}
              submitting={submitting}
              confirmThisRow={armedRow === m.game}
              onArmConfirm={() => setArmedRow(m.game)}
            />
          ))}
        </div>

        {error && (
          <div className="mt-3 text-[12px] text-red-300">{error}</div>
        )}
      </div>
    </div>
  );

  return portalHost ? createPortal(node, portalHost) : node;
}
